import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Check, Loader2, Sparkles, CheckCircle2 } from "lucide-react";

export default function AdminPagos() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [params, setParams] = useSearchParams();
  const [payState, setPayState] = useState(null); // {status, msg}

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/payments/packages"); setPackages(data || []); }
      finally { setLoading(false); }
    })();
  }, []);

  const poll = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 6) { setPayState({ status: "pending", msg: "El pago sigue procesándose. Revisa más tarde." }); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") { setPayState({ status: "paid", msg: "¡Pago exitoso! Tu plan está activo. 🎉" }); return; }
      if (data.status === "expired") { setPayState({ status: "error", msg: "La sesión de pago expiró. Intenta de nuevo." }); return; }
      setPayState({ status: "pending", msg: "Procesando tu pago…" });
      setTimeout(() => poll(sessionId, attempts + 1), 2000);
    } catch {
      setPayState({ status: "error", msg: "No se pudo verificar el pago." });
    }
  }, []);

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) {
      setPayState({ status: "pending", msg: "Verificando tu pago…" });
      poll(sid);
      params.delete("session_id");
      setParams(params, { replace: true });
    }
  }, []); // eslint-disable-line

  const subscribe = async (pkg) => {
    setBusy(pkg.id);
    try {
      const { data } = await api.post("/payments/checkout", { package_id: pkg.id, origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch {
      toast.error("No se pudo iniciar el pago.");
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-pagos-page">
      <div>
        <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#A43790]">Pagos y Suscripciones</p>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#1F2547] flex items-center gap-2">
          <CreditCard className="text-[#A43790]" /> Planes
        </h1>
        <p className="text-sm text-[#6E7591] mt-1">Elige el plan que mejor se adapta a tu institución.</p>
      </div>

      {payState && (
        <div className={`jasq-card p-4 flex items-center gap-3 ${payState.status === "paid" ? "border-[#16A34A]" : payState.status === "error" ? "border-[#D63384]" : "border-[#F5B82E]"}`} data-testid="payment-status">
          {payState.status === "paid" ? <CheckCircle2 className="text-[#16A34A]" /> : payState.status === "error" ? <span className="text-[#D63384]">✕</span> : <Loader2 className="animate-spin text-[#F5B82E]" />}
          <span className="font-bold text-[#1F2547]">{payState.msg}</span>
        </div>
      )}

      {loading ? (
        <div className="jasq-card p-10 text-center text-sm text-[#6E7591]">Cargando planes…</div>
      ) : (
        <div className="grid md:grid-cols-3 gap-4" data-testid="packages-grid">
          {packages.map((p, i) => {
            const featured = p.id === "pro";
            return (
              <div key={p.id} className={`jasq-card p-6 flex flex-col relative ${featured ? "border-2 border-[#A43790] shadow-lg" : ""}`} data-testid={`package-${p.id}`}>
                {featured && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase bg-[#A43790] text-white rounded-full px-3 py-1 flex items-center gap-1"><Sparkles size={11} /> Recomendado</span>}
                <h3 className="font-display text-xl font-extrabold text-[#1F2547]">{p.name}</h3>
                <p className="mt-2"><span className="text-4xl font-extrabold text-[#A43790]">${p.amount}</span><span className="text-sm text-[#6E7591]"> /mes</span></p>
                <p className="text-sm text-[#6E7591] mt-3 flex-1">{p.desc}</p>
                <button onClick={() => subscribe(p)} disabled={busy === p.id} data-testid={`subscribe-${p.id}`}
                  className={`mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-2xl font-bold text-sm transition-colors disabled:opacity-60 ${featured ? "bg-[#A43790] text-white hover:bg-[#D63384]" : "bg-[#1F2547] text-white hover:bg-[#2C49A0]"}`}>
                  {busy === p.id ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Suscribirme
                </button>
              </div>
            );
          })}
        </div>
      )}
      <p className="text-xs text-[#A6ACC1] text-center">Pagos seguros procesados por Stripe. Modo de prueba activo.</p>
    </div>
  );
}
